import { Formik, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Form, Tab, Tabs } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { generatePassword } from '../../../../../../../util/auth.util'
import { getEmployeeNameWithEmail } from '../../../../../../../util/table.util'
import { getFinalValuesFromTaskParameterFunction, getFormikSchema } from '../../../../config'
import { buildInitialValues, getValidationSchema } from '../../../../modals/common/PasswordSettingsSection/fieldset'
import { cloneChildComponents, formatDomainOptions } from '../../../../utils/helpers'
import { getGwsDomainValue, mapGwsPasswordFormValues } from '../../utils/mapGwsPasswordFormValues'
import AddUserAccountTab from './AddUserAccountTab/AddUserAccountTab'
import AddUserEmployeeTab from './AddUserEmployeeTab/AddUserEmployeeTab'
import AddUserLicenseTab from './AddUserLicenseTab/AddUserLicenseTab'
import AddUserReviewTab from './AddUserReviewTab/AddUserReviewTab'

const TAB_KEYS = {
  ADD_EMPLOYEE: 'add-employee',
  ACCOUNT_DETAILS: 'account-details',
  ASSIGN_LICENSE: 'assign-license',
  REVIEW: 'review',
}

const SUCCESS_ACTION_IDS = {
  EMPLOYEE_CREATED: 'ENGAGE.EMPLOYEE_CREATED',
  ACCOUNT_CREATED: 'ENGAGE.ACCOUNT_CREATED',
  LICENSES_ASSIGNED: 'ENGAGE.LICENSES_ASSIGNED',
  ADDED_TO_GROUP: 'ENGAGE.ADDED_TO_GROUP',
}

const syncAddUserSubmitDisabledPropTypes = {
  activeKey: PropTypes.string.isRequired,
  hasExistingGwsAccount: PropTypes.bool,
  hasActiveGwsAccount: PropTypes.bool,
  setDisableSubmit: PropTypes.func.isRequired,
}

function SyncAddUserSubmitDisabled({
  activeKey,
  hasExistingGwsAccount = false,
  hasActiveGwsAccount = false,
  setDisableSubmit,
}) {
  const { values } = useFormikContext()

  useEffect(() => {
    if (hasActiveGwsAccount) {
      setDisableSubmit(true)
      return
    }

    if (activeKey === TAB_KEYS.ADD_EMPLOYEE) {
      setDisableSubmit(!values.firstName || !values.lastName || !values.department || !values.jobTitle)
      return
    }

    if (activeKey === TAB_KEYS.ACCOUNT_DETAILS) {
      const missingUsername = !hasExistingGwsAccount && (!values.username || !values.domain)
      const missingPassword = values.isResettingPassword && !values.isAutoGeneratingPassword && !values.newPassword
      const missingSendToEmployee =
        values.isResettingPassword &&
        values.sendPasswordOnCompletion &&
        !values.isSendingPasswordToMe &&
        !values.sendToEmployeeEmail
      setDisableSubmit(Boolean(missingUsername || missingPassword || missingSendToEmployee))
      return
    }

    if (activeKey === TAB_KEYS.ASSIGN_LICENSE) {
      setDisableSubmit(!values.licenses?.length)
      return
    }

    setDisableSubmit(false)
  }, [activeKey, hasActiveGwsAccount, hasExistingGwsAccount, setDisableSubmit, values])

  return null
}

SyncAddUserSubmitDisabled.propTypes = syncAddUserSubmitDisabledPropTypes

function AddUser(props) {
  const { children, ...rest } = props
  const {
    intl,
    show,
    action,
    taskParameters,
    setTaskParameters,
    initialValues,
    allAugmenttEmployees,
    allLicensedEmployees,
    onActionSubmit,
    selectedEmployee,
    setDisableSubmit,
    setActionData,
    actionData,
    setCompletedActions,
  } = rest

  const formikRef = useRef()
  const autoGeneratedPassword = useMemo(() => generatePassword(), [])
  const hasExistingGwsAccount = Boolean(selectedEmployee?.employeeIdentifier)
  const hasActiveGwsAccount = hasExistingGwsAccount && selectedEmployee?.enabled !== false

  const [domainOptions, setDomains] = useState([])
  const [groupOptions, setGroupOptions] = useState([])
  const [licenses, setLicenses] = useState([])
  const [activeKey, setActiveKey] = useState(TAB_KEYS.ADD_EMPLOYEE)
  const [newEmployee, setNewEmployee] = useState()

  const employeeEmailOptions = useMemo(
    () =>
      (allAugmenttEmployees || [])
        .filter((item) => !item.ignored && item.email)
        .map((item) => ({
          label: getEmployeeNameWithEmail(item),
          value: item.email,
        }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)),
    [allAugmenttEmployees],
  )

  const formInitialValues = useMemo(
    () => ({
      ...initialValues,
      ...buildInitialValues({
        currentUserEmail: initialValues?.email,
        autoGeneratedPassword,
      }),
      phone: '',
      groups: [],
      licenses: [],
      isResettingPassword: !hasExistingGwsAccount,
      domain: initialValues?.domain || null,
    }),
    [autoGeneratedPassword, hasExistingGwsAccount, initialValues],
  )

  const validationSchema = useMemo(() => {
    const addUserSchema = getFormikSchema(action, intl, {
      isAutoGeneratingPassword: true,
      activeKey,
    })
    const passwordSchema = getValidationSchema(intl)

    if (addUserSchema && passwordSchema) {
      return addUserSchema.concat(passwordSchema)
    }

    return addUserSchema || passwordSchema
  }, [action, activeKey, intl])

  useEffect(() => {
    if (!show) {
      return
    }

    if (selectedEmployee) {
      setActiveKey(TAB_KEYS.ACCOUNT_DETAILS)
    }

    formatDomainOptions(allLicensedEmployees, setDomains, {
      adminDomainsOnly: false,
    })

    const options = []
    allLicensedEmployees.forEach((employee) => {
      ;(employee.groups || []).forEach((group) => {
        options.push({
          value: group.id,
          label: group.name,
        })
      })
    })

    const uniqueGroupOptions = options
      .filter((option, index, list) => list.findIndex((item) => item.label === option.label) === index)
      .sort((a, b) => a.label.localeCompare(b.label))
    setGroupOptions(uniqueGroupOptions)

    const licenseMap = new Map()
    allLicensedEmployees.forEach((employee) => {
      ;(employee.licenseType || []).forEach((license) => {
        if (!licenseMap.has(license.id)) {
          licenseMap.set(license.id, { ...license })
        }
      })
    })
    setLicenses(Array.from(licenseMap.values()))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!licenses?.length || !formikRef.current) {
      return
    }

    const currentLicenseIds = formikRef.current.values?.licenses
    if (currentLicenseIds?.length) {
      return
    }

    const primaryLicense = licenses.find((license) => license?.isPrimary)
    if (primaryLicense) {
      formikRef.current.setFieldValue('licenses', [primaryLicense.id])
    }
  }, [licenses])

  useEffect(() => {
    if (newEmployee) {
      setActionData({
        ...actionData,
        newEmployee,
        newEmployeeName: `${newEmployee.firstName} ${newEmployee.lastName}`,
      })
    }
  }, [newEmployee]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(actionData || {}, 'isResettingPassword')) {
      let finalTaskParameters = taskParameters
      const taskParameterFields = {
        selectedEmployee: selectedEmployee || newEmployee,
        actionData,
      }

      if (typeof finalTaskParameters === 'function') {
        try {
          finalTaskParameters = getFinalValuesFromTaskParameterFunction(
            action,
            finalTaskParameters,
            taskParameterFields,
          )
        } catch (error) {
          console.error(error)
        }
        setTaskParameters(finalTaskParameters)
      } else {
        console.error('Error - taskParameters should be a function')
      }

      onActionSubmit(action, finalTaskParameters)
    }
  }, [actionData]) // eslint-disable-line react-hooks/exhaustive-deps

  const next = (values) => {
    if (hasActiveGwsAccount) {
      return
    }

    if (activeKey === TAB_KEYS.ADD_EMPLOYEE) {
      setActiveKey(TAB_KEYS.ACCOUNT_DETAILS)
      setNewEmployee(values)
      return
    }

    if (activeKey === TAB_KEYS.ACCOUNT_DETAILS) {
      setActiveKey(TAB_KEYS.ASSIGN_LICENSE)
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, SUCCESS_ACTION_IDS.ACCOUNT_CREATED])
      setActionData({
        ...actionData,
        newEmployeeEmail: selectedEmployee?.email || `${values?.username}@${getGwsDomainValue(values?.domain)}`,
      })
      return
    }

    if (activeKey === TAB_KEYS.ASSIGN_LICENSE) {
      setActiveKey(TAB_KEYS.REVIEW)
      return
    }

    const newCompletedActions = []
    const newActionData = {
      values: {
        ...values,
        ...mapGwsPasswordFormValues(values),
        domain: { value: getGwsDomainValue(values.domain) },
      },
      phoneNumber: values.phone,
      isResettingPassword: values.isResettingPassword,
    }

    if (newEmployee) {
      newCompletedActions.push(SUCCESS_ACTION_IDS.EMPLOYEE_CREATED)
    }

    if (values.licenses?.length) {
      newCompletedActions.push(SUCCESS_ACTION_IDS.LICENSES_ASSIGNED)
      newActionData.licenseIdsToAdd = values.licenses
      newActionData.licensesAssigned = licenses.filter((license) => values.licenses.includes(license.id))
    }

    if (values.groups?.length) {
      newCompletedActions.push(SUCCESS_ACTION_IDS.ADDED_TO_GROUP)
      newActionData.groupIdsToAdd = values.groups
      newActionData.groups = values.groups.map((groupId) => {
        const option = groupOptions.find((group) => group.value === groupId)
        return { name: option?.label }
      })
    }

    setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, ...newCompletedActions])
    setActionData({ ...actionData, ...newActionData })
  }

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      validationSchema={validationSchema || undefined}
      initialValues={formInitialValues}
      onSubmit={(values) => next(values)}
    >
      {({ handleSubmit, values }) => (
        <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
          <Form>
            <SyncAddUserSubmitDisabled
              activeKey={activeKey}
              hasExistingGwsAccount={hasExistingGwsAccount}
              hasActiveGwsAccount={hasActiveGwsAccount}
              setDisableSubmit={setDisableSubmit}
            />
            {hasActiveGwsAccount && (
              <Alert variant="warning">
                <FormattedMessage id="ENGAGE.GWS_ACTIVE_ACCOUNT_EXISTS" />
              </Alert>
            )}
            <Tabs activeKey={activeKey} onSelect={(key) => setActiveKey(key)} className="nav-fill engage-tabs">
              {!selectedEmployee && (
                <Tab
                  eventKey={TAB_KEYS.ADD_EMPLOYEE}
                  tabClassName="engage-tab"
                  title={
                    <p>
                      <FormattedMessage id="ADMINISTRATION.ADD_EMPLOYEE" />
                    </p>
                  }
                >
                  <AddUserEmployeeTab />
                </Tab>
              )}
              <Tab
                eventKey={TAB_KEYS.ACCOUNT_DETAILS}
                tabClassName="engage-tab"
                title={
                  <p>
                    <FormattedMessage id="ENGAGE.ACCOUNT_DETAILS_C" />
                  </p>
                }
                disabled={activeKey === TAB_KEYS.ADD_EMPLOYEE}
              >
                <AddUserAccountTab
                  selectedEmployee={selectedEmployee}
                  domainOptions={domainOptions}
                  groupOptions={groupOptions}
                  employeeEmailOptions={employeeEmailOptions}
                  autoGeneratedPassword={autoGeneratedPassword}
                  canSkipPasswordReset={hasExistingGwsAccount}
                />
              </Tab>
              <Tab
                eventKey={TAB_KEYS.ASSIGN_LICENSE}
                tabClassName="engage-tab"
                title={
                  <p>
                    <FormattedMessage id="ADMINISTRATION.ASSIGN_LICENSE_C" />
                  </p>
                }
                disabled={activeKey === TAB_KEYS.ADD_EMPLOYEE || activeKey === TAB_KEYS.ACCOUNT_DETAILS}
              >
                <AddUserLicenseTab licenses={licenses} />
              </Tab>
              <Tab
                eventKey={TAB_KEYS.REVIEW}
                tabClassName="engage-tab"
                title={
                  <p>
                    <FormattedMessage id="ENGAGE.REVIEW_AND_FINISH" />
                  </p>
                }
                disabled={activeKey !== TAB_KEYS.REVIEW}
              >
                <AddUserReviewTab
                  selectedEmployee={selectedEmployee}
                  values={values}
                  licenses={licenses}
                  onEditTab={setActiveKey}
                />
              </Tab>
            </Tabs>

            {cloneChildComponents(
              {
                ...rest,
                handleSubmit,
                values,
                formikRef,
              },
              children,
            )}
          </Form>
        </PerfectScrollbar>
      )}
    </Formik>
  )
}

const addUserPropTypes = {
  children: PropTypes.node,
  intl: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  show: PropTypes.bool,
  action: PropTypes.string,
  taskParameters: PropTypes.oneOfType([PropTypes.func, PropTypes.object]), // eslint-disable-line react/forbid-prop-types
  setTaskParameters: PropTypes.func,
  initialValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  allAugmenttEmployees: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
  allLicensedEmployees: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
  onActionSubmit: PropTypes.func,
  selectedEmployee: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  setDisableSubmit: PropTypes.func,
  setActionData: PropTypes.func,
  actionData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  setCompletedActions: PropTypes.func,
}

AddUser.propTypes = addUserPropTypes

export default AddUser
